import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import {
  fetchCourtDecision,
  fetchCourtDecisionType,
} from "services/basedataServices";
import { addFinalVerdict, editFinalVerdict } from "services/caseServices";
import {
  CaseCreation,
  CaseDefendantLink,
  CountDefendantsList,
  CourtDecision,
  CourtDecisionType,
  FinalVerdict,
  OpeniningCaseFile,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  action: "Add" | "Edit";
  caseData: CaseCreation;
  // When editing, the specific previously-added verdict to update.
  finalVerdict?: FinalVerdict;
  onClose: (refetch: boolean) => void;
};
const defaultValue = {
  id: 0,
  opening_case_file: null,
  defendants: [],
  prosecutor_registration_number: "",
  incident_registration_no: "",
  crime_refernce_number: "",
  court_registration_number: "",
  court_decision: "",
  court_decision_type: "",
  court_decision_amount: "",
  prosecutor_feedback_on_decision: "",
  court_decision_summary: "",
};
const AddFinalVerdict: FC<Props> = ({
  caseData,
  action,
  finalVerdict,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState(defaultValue);
  const [defendantId, setDefendantId] = useState<any>();
  const [countDefendant, setCountDefendant] = useState<any>();
  const [isAlternateCharge, setIsAlternateCharge] = useState<string>("false");
  const [baseData, setBaseData] = useState<{
    courtDecision: CourtDecision[];
    courtDecisionType: CourtDecisionType[];
  }>({
    courtDecision: [],
    courtDecisionType: [],
  });
  const [countIndex, setCountIndex] = useState<number>();
  const [countDefendantList, setCountDefendantList] =
    useState<{ defendant: CaseDefendantLink; count: CountDefendantsList }[]>();

  const fetchBaseData = () => {
    fetchCourtDecision().then((data) =>
      setBaseData((prev) => ({ ...prev, courtDecision: data })),
    );
    fetchCourtDecisionType().then((data) =>
      setBaseData((prev) => ({ ...prev, courtDecisionType: data })),
    );
  };
  useEffect(() => {
    fetchBaseData();
  }, []);

  useEffect(() => {
    if (caseData) {
      const opening = caseData?.opening_case_files?.[0] as OpeniningCaseFile;

      setFormState({
        ...defaultValue,
        prosecutor_registration_number:
          caseData?.prosecutor_registration_number || "",
        incident_registration_no: caseData?.incident_registration_no || "",
        crime_refernce_number: caseData?.crime_refernce_number || "",
        court_registration_number: opening?.court_registration_number || "",
      });
    }
  }, [caseData]);

  // Editing a previously-added decision: pre-fill the form from that verdict
  // and lock its defendant/count (the picker excludes already-used pairs).
  useEffect(() => {
    if (action !== "Edit" || !finalVerdict) return;
    const asStr = (v: any) =>
      v && typeof v === "object" ? v?.court_decision ?? "" : v ?? "";
    const idOf = (v: any) =>
      v && typeof v === "object" ? v?.id : v ?? null;

    setFormState((prev) => ({
      ...prev,
      id: finalVerdict?.id ?? 0,
      court_decision: asStr(finalVerdict?.court_decision),
      court_decision_type: asStr(finalVerdict?.court_decision_type),
      court_decision_amount: finalVerdict?.court_decision_amount ?? "",
      prosecutor_feedback_on_decision:
        finalVerdict?.prosecutor_feedback_on_decision ?? "",
      court_decision_summary: finalVerdict?.court_decision_summary ?? "",
    }));
    setIsAlternateCharge(String(finalVerdict?.is_alternate_charge ?? "false"));
    setCountDefendant(
      finalVerdict?.count_defendant_info?.id ??
        idOf(finalVerdict?.count_defendant),
    );
    setDefendantId(
      finalVerdict?.case_defendant_link_info?.id ??
        idOf(finalVerdict?.case_defendant_link),
    );
  }, [action, finalVerdict]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    // if (!formState.first_name) return "First Name is Required.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    // Submit the FormData
    const submitAction = action === "Edit" ? editFinalVerdict : addFinalVerdict;
    submitAction({
      ...formState,
      opening_case_file: caseData?.opening_case_files?.[0]?.id ?? 0,
      case: caseData?.id ?? 0,
      count_defendant: countDefendant || null,
      is_alternate_charge: isAlternateCharge,
      case_defendant_link: defendantId || null,
    })
      .then(() => {
        showTooltip(
          action === "Edit"
            ? "Successfully Updated!"
            : "Successfully Submitted!",
          "success",
        );
        setFormState(defaultValue);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  useEffect(() => {
    if (caseData?.counts && caseData?.FinalVerdict) {
      const data =
        caseData?.PreJudgementEntry?.filter(
          (p) => p?.decision !== "not_gulity",
        )?.map((p) => ({
          defendant: p?.defendant_info,
          count: p?.count_defendant_info,
          has_alternate: p?.has_alternate,
          count_updated: p?.decision === "update_count",
        })) || [];

      // Filter out already used defendant+count+has_alternate pairs
      let filtered = data.filter((a) => {
        return (
          a.defendant !== undefined &&
          a.count !== null &&
          !caseData.FinalVerdict?.some(
            (f) =>
              f?.case_defendant_link_info?.id === a?.defendant?.id &&
              f?.count_defendant_info?.id === a?.count?.id &&
              Boolean(f?.has_alternate) === Boolean(a?.has_alternate),
          )
        );
      }) as {
        defendant: CaseDefendantLink;
        count: CountDefendantsList;
        has_alternate?: boolean;
        count_updated?: boolean;
      }[];

      setCountDefendantList(filtered);
    }
  }, [caseData]);

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold text-[#AA232F]">
          Case Final Result Form For
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          Lorem Ipsum is a placeholder text commonly used in the design and
          publishing industries as a temporary filler.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="mt-4">
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Select Count Defendant
              </label>
              {action === "Edit" && finalVerdict ? (
                // Editing an existing decision — show the locked defendant/count.
                <div className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2">
                  {finalVerdict?.case_defendant_link_info?.defendant_info
                    ?.first_name || ""}{" "}
                  {finalVerdict?.case_defendant_link_info?.defendant_info
                    ?.middle_name || ""}{" "}
                  {finalVerdict?.case_defendant_link_info?.defendant_info
                    ?.last_name || ""}{" "}
                  : {finalVerdict?.count_defendant_info?.count_number || ""}
                  {finalVerdict?.has_alternate ? " (Alternate)" : ""}
                </div>
              ) : (
                <select
                  className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                  value={countIndex}
                  onChange={(e) => {
                    const index = Number(e.target.value);
                    const value = countDefendantList
                      ? countDefendantList[index]
                      : null;
                    setCountIndex(countIndex);
                    setDefendantId(value?.defendant?.id);
                    setCountDefendant(value?.count?.id);
                  }}
                >
                  <option value={""}>Select Defendant</option>
                  {countDefendantList?.map((def: any, idx: number) => (
                    <option key={def.id} value={idx}>
                      {def?.defendant.defendant_info?.first_name}{" "}
                      {def?.defendant.defendant_info?.middle_name}{" "}
                      {def?.defendant.defendant_info?.last_name} :{" "}
                      {def.count?.count_number}{" "}
                      {def?.has_alternate ? "(Alternate)" : ""}
                      {def?.count_updated ? "(Updated)" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {/* <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Select Defendant
              </label>
              <select
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                value={defendantId}
                onChange={(e) => {
                  const value = e.target.value;
                  setDefendantId(value);
                }}
              >
                <option value={""}>Select Defendant</option>
                {caseData?.case_defendant_links?.map((def: any) => (
                  <option key={def.id} value={def.id}>
                    Defendant {def.defendant_number} :{" "}
                    {def.defendant_info?.first_name}{" "}
                    {def.defendant_info?.middle_name}{" "}
                    {def.defendant_info?.last_name}
                  </option>
                ))}
              </select>
            </div> */}
            {/* <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Select Count Defendnt
              </label>
              <select
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                value={countDefendant}
                onChange={(e) => {
                  const value = e.target.value;
                  setCountDefendant(value);
                }}
              >
                <option value={""}>Select Court Defendant</option>
                {Array.isArray(caseData?.counts) &&
                  caseData?.counts.map((count, index) => (
                    <option key={index} value={count.id}>
                      {count.count_number}
                    </option>
                  ))}
              </select>
            </div> */}

            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Has Alternate
              </label>
              <select
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="has_alternate"
                value={isAlternateCharge}
                onChange={(e) => {
                  setIsAlternateCharge(e.target.value);
                }}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Court Decision Amount
              </label>
              <input
                type="text"
                placeholder="Court decision amount here..."
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="court_decision_amount"
                value={formState?.court_decision_amount}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Select Corut Decision
              </label>
              <select
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                name="court_decision"
                value={formState?.court_decision || ""}
                onChange={handleChange}
              >
                <option value={""}>Select Court Decision</option>
                {baseData?.courtDecision.map((data) => (
                  <option key={data?.id} value={data?.court_decision}>
                    {data?.court_decision}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Select Corut Decision Type
              </label>
              <select
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                name="court_decision_type"
                value={formState?.court_decision_type || ""}
                onChange={handleChange}
              >
                <option value={""}>Select Court Decision Type</option>
                {baseData?.courtDecisionType.map((data) => (
                  <option key={data?.id} value={data?.court_decision}>
                    {data?.court_decision}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Prosecutor Feedback on Decision
              </label>
              <textarea
                rows={4}
                placeholder="Prosecutor feedback on decision here..."
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="prosecutor_feedback_on_decision"
                value={formState?.prosecutor_feedback_on_decision}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Court Decision Summary
              </label>
              <textarea
                rows={4}
                placeholder="Prosecutor feedback on decision here..."
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="court_decision_summary"
                value={formState?.court_decision_summary}
                onChange={handleChange}
              />
            </div>
            {/* <div className="p-4 border border-[#D9D9D9] rounded-2xl mt-4">
              <div className="flex items-center align-middle border border-[#212121] rounded-md bg-[#21212133] p-4">
                <input
                  type="checkbox"
                  id="appealCheckbox"
                  className="w-5 h-5 text-[#002855] border-gray-300 rounded focus:ring-[#212121] focus:ring-2"
                  // name="verdict_fine_paid"
                  // value={formData.verdict_fine_paid}
                  onChange={(e) => {
                    // setSelectedcaseData(courtFileFromServer);
                    // setActivePage("appeal-request");
                  }}
                />
                <label
                  htmlFor="appealCheckbox"
                  className="ml-3 mb-2 text-sm font-medium text-gray-700"
                >
                  Do you want to appeal
                </label>
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Appeal Reason Short Description
                </label>
                <textarea
                  rows={4}
                  placeholder="Appeal reason here ....."
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                />
              </div>
            </div> */}
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFinalVerdict;
